"use client";

import { useCurrency } from "@/context/CurrencyContext";
import { DEFAULT_CURRENCY, formatMoney, isMinorAmount } from "@/lib/money";
import { RATE_PROVIDER_LABEL } from "@/lib/exchangeRates";
import { cn } from "@/lib/utils";

interface ConvertedPriceProps {
  /** Original price in integer minor units (pesewas/cents). */
  price: number | undefined | null;
  /** Original currency code (e.g. "GHS", "USD"). */
  currencyCode?: string;
  /** Optional suffix like "/ Month". */
  suffix?: string;
  /** CSS class for the primary price text. */
  className?: string;
  /** CSS class for the converted line. */
  convertedClassName?: string;
  /** Hide the "Rates by …" attribution (e.g. on small cards). */
  hideAttribution?: boolean;
  /** Show the conversion inline (same line) instead of below. */
  inline?: boolean;
}

/**
 * Displays a listing price given in integer minor units.
 *
 * - Shows the original price first.
 * - If the user's preferred currency differs, shows "≈ <converted> <currency>"
 *   on a second line with a subtle attribution label.
 * - If currencies match, shows just the original price (no conversion).
 */
export default function ConvertedPrice({
  price,
  currencyCode,
  suffix,
  className,
  convertedClassName,
  hideAttribution = false,
  inline = false,
}: ConvertedPriceProps) {
  const { convert, preferredCurrency } = useCurrency();

  // A missing price shows "—" (never "₵ 0") and is not converted.
  const original = formatMoney(isMinorAmount(price) ? price : null, currencyCode);
  const from = (currencyCode || DEFAULT_CURRENCY).toUpperCase();
  // Exchange rates are linear, so converting minor units gives the target's
  // minor units (all supported currencies have 2 decimals). The result is a
  // display-only estimate, rounded to a whole major unit as before.
  const result = isMinorAmount(price) ? convert(price, from) : null;

  // No conversion needed or rates unavailable
  if (!result || !result.isConverted) {
    return (
      <span className={className}>
        {original}
        {suffix && <span>{suffix}</span>}
      </span>
    );
  }

  const convertedFormatted = formatMoney(
    Math.round(result.amount / 100) * 100,
    preferredCurrency,
  );

  if (inline) {
    return (
      <span className={className}>
        {original}
        {suffix && <span>{suffix}</span>}
        <span
          className={cn(
            "ml-1.5 text-[0.75em] text-gray-500 dark:text-gray-400 font-medium",
            convertedClassName,
          )}
          title={`Rates by ${RATE_PROVIDER_LABEL}`}
        >
          ≈ {convertedFormatted}
        </span>
      </span>
    );
  }

  return (
    <span className={cn("flex flex-col", className)}>
      <span>
        {original}
        {suffix && <span>{suffix}</span>}
      </span>
      <span
        className={cn(
          "text-sm text-gray-500 dark:text-gray-400 font-medium leading-tight",
          convertedClassName,
        )}
      >
        ≈ {convertedFormatted}
        {!hideAttribution && (
          <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">
            ({RATE_PROVIDER_LABEL})
          </span>
        )}
      </span>
    </span>
  );
}
